import React, { useState } from 'react';
import { 
  User, Mail, Phone, MapPin, Calendar, Shield, 
  Camera, Save, Edit2, X, CheckCircle, AlertCircle,
  Globe, Briefcase, Clock, Smartphone, Lock, Key,
  Eye, EyeOff, Upload, Trash2, Plus, ChevronRight,
  Award, Crown, Star, Heart, Share2, Settings
} from 'lucide-react';
import { Link } from 'react-router-dom';

const AdminProfile = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');

  // Profile Data
  const [profile, setProfile] = useState({
    name: 'Admin User',
    email: 'admin@paudhawala.com',
    phone: '+91 99558 59697',
    role: 'Super Admin',
    department: 'Management',
    location: 'Hajipur, Bihar, India',
    bio: 'Experienced e-commerce administrator with over 5 years of experience in plant nursery management.',
    joinDate: 'Jan 15, 2023',
    lastActive: 'Today, 10:30 AM',
    avatar: 'A'
  });

  const handleSave = () => {
    setSaving(true);
    setTimeout(() => {
      setSaving(false);
      setIsEditing(false);
      alert('Profile updated successfully!');
    }, 1500);
  };

  const handleChangePassword = () => {
    alert('Password changed successfully!');
    setShowChangePassword(false);
  };

  const stats = [
    { label: 'Total Logins', value: '245', icon: Clock, color: '#10b981' },
    { label: 'Active Sessions', value: '2', icon: Smartphone, color: '#3b82f6' },
    { label: 'Role', value: 'Super Admin', icon: Shield, color: '#8b5cf6' },
    { label: 'Joined', value: 'Jan 2023', icon: Calendar, color: '#f59e0b' },
  ];

  const recentActivities = [
    { action: 'Updated product inventory', time: '2 hours ago', type: 'edit' },
    { action: 'Approved seller registration', time: '5 hours ago', type: 'approve' },
    { action: 'Processed payout request', time: '1 day ago', type: 'payment' },
    { action: 'Changed system settings', time: '2 days ago', type: 'settings' },
  ];

  const permissions = [
    { name: 'User Management', granted: true },
    { name: 'Product Management', granted: true },
    { name: 'Order Management', granted: true },
    { name: 'Seller Management', granted: true },
    { name: 'Payment Processing', granted: true },
    { name: 'System Settings', granted: true },
    { name: 'Report Generation', granted: true },
    { name: 'Analytics View', granted: true },
  ];

  return (
    <div className="admin-profile-modern">
      {/* Header */}
      <div className="profile-header">
        <div>
          <h1 className="page-title">Admin Profile</h1>
          <p className="page-subtitle">Manage your account settings and preferences</p>
        </div>
        <div className="header-actions">
          {!isEditing ? (
            <button className="btn-edit" onClick={() => setIsEditing(true)}>
              <Edit2 size={16} />
              Edit Profile
            </button>
          ) : (
            <div className="action-buttons">
              <button className="btn-cancel" onClick={() => setIsEditing(false)}>
                <X size={16} />
                Cancel
              </button>
              <button className="btn-save" onClick={handleSave} disabled={saving}>
                {saving ? (
                  <>
                    <div className="spinner-small"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="profile-layout">
        {/* Sidebar */}
        <div className="profile-sidebar">
          {/* Avatar Section */}
          <div className="avatar-section">
            <div className="avatar-container">
              <div className="avatar">
                <span>{profile.avatar}</span>
              </div>
              {isEditing && (
                <button className="avatar-upload">
                  <Camera size={16} />
                </button>
              )}
            </div>
            <div className="avatar-info">
              <h3>{profile.name}</h3>
              <p>{profile.role}</p>
            </div>
          </div>

          {/* Stats Section */}
          <div className="stats-section">
            {stats.map((stat, idx) => {
              const Icon = stat.icon;
              return (
                <div key={idx} className="stat-item">
                  <div className="stat-icon" style={{ backgroundColor: `${stat.color}15` }}>
                    <Icon size={16} color={stat.color} />
                  </div>
                  <div className="stat-info">
                    <span className="stat-value">{stat.value}</span>
                    <span className="stat-label">{stat.label}</span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Navigation Tabs */}
          <div className="profile-nav">
            <button 
              className={`nav-item ${activeTab === 'profile' ? 'active' : ''}`}
              onClick={() => setActiveTab('profile')}
            >
              <User size={18} />
              Profile Information
            </button>
            <button 
              className={`nav-item ${activeTab === 'security' ? 'active' : ''}`}
              onClick={() => setActiveTab('security')}
            >
              <Shield size={18} />
              Security
            </button>
            <button 
              className={`nav-item ${activeTab === 'activity' ? 'active' : ''}`}
              onClick={() => setActiveTab('activity')}
            >
              <Clock size={18} />
              Activity Log
            </button>
            <button 
              className={`nav-item ${activeTab === 'permissions' ? 'active' : ''}`}
              onClick={() => setActiveTab('permissions')}
            >
              <Key size={18} />
              Permissions
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="profile-content">
          {/* Profile Information Tab */}
          {activeTab === 'profile' && (
            <div className="content-card">
              <div className="card-header">
                <h3>Profile Information</h3>
                <p>Update your personal information and contact details</p>
              </div>
              <div className="card-body">
                <div className="form-group">
                  <label>Full Name</label>
                  <input 
                    type="text" 
                    className="form-input" 
                    value={profile.name}
                    onChange={(e) => setProfile({...profile, name: e.target.value})}
                    disabled={!isEditing}
                  />
                </div>
                <div className="form-group">
                  <label>Email Address</label>
                  <input 
                    type="email" 
                    className="form-input" 
                    value={profile.email}
                    onChange={(e) => setProfile({...profile, email: e.target.value})}
                    disabled={!isEditing}
                  />
                </div>
                <div className="form-group">
                  <label>Phone Number</label>
                  <input 
                    type="tel" 
                    className="form-input" 
                    value={profile.phone}
                    onChange={(e) => setProfile({...profile, phone: e.target.value})}
                    disabled={!isEditing}
                  />
                </div>
                <div className="form-group">
                  <label>Department</label>
                  <input 
                    type="text" 
                    className="form-input" 
                    value={profile.department}
                    onChange={(e) => setProfile({...profile, department: e.target.value})}
                    disabled={!isEditing}
                  />
                </div>
                <div className="form-group">
                  <label>Location</label>
                  <input 
                    type="text" 
                    className="form-input" 
                    value={profile.location}
                    onChange={(e) => setProfile({...profile, location: e.target.value})}
                    disabled={!isEditing}
                  />
                </div>
                <div className="form-group">
                  <label>Bio</label>
                  <textarea 
                    className="form-textarea" 
                    rows="4"
                    value={profile.bio}
                    onChange={(e) => setProfile({...profile, bio: e.target.value})}
                    disabled={!isEditing}
                  />
                </div>
                <div className="info-row">
                  <div className="info-item">
                    <Calendar size={16} />
                    <span>Joined: {profile.joinDate}</span>
                  </div>
                  <div className="info-item">
                    <Clock size={16} />
                    <span>Last Active: {profile.lastActive}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Security Tab */}
          {activeTab === 'security' && (
            <div className="content-card">
              <div className="card-header">
                <h3>Security Settings</h3>
                <p>Manage your password and security preferences</p>
              </div>
              <div className="card-body">
                <div className="security-option">
                  <div>
                    <h4>Change Password</h4>
                    <p>Update your password to keep your account secure</p>
                  </div>
                  <button className="btn-outline" onClick={() => setShowChangePassword(true)}>
                    Change Password
                  </button>
                </div>
                <div className="security-option">
                  <div>
                    <h4>Two-Factor Authentication</h4>
                    <p>Add an extra layer of security to your account</p>
                  </div>
                  <label className="toggle-switch">
                    <input type="checkbox" />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
                <div className="security-option">
                  <div>
                    <h4>Session Management</h4>
                    <p>Manage active sessions and devices</p>
                  </div>
                  <button className="btn-outline">Manage Sessions</button>
                </div>
              </div>
            </div>
          )}

          {/* Activity Log Tab */}
          {activeTab === 'activity' && (
            <div className="content-card">
              <div className="card-header">
                <h3>Recent Activity</h3>
                <p>Your recent actions and system activities</p>
              </div>
              <div className="card-body">
                <div className="activity-list">
                  {recentActivities.map((activity, idx) => (
                    <div key={idx} className="activity-item">
                      <div className="activity-icon">
                        <div className={`icon-dot ${activity.type}`}></div>
                      </div>
                      <div className="activity-content">
                        <p>{activity.action}</p>
                        <span>{activity.time}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Permissions Tab */}
          {activeTab === 'permissions' && (
            <div className="content-card">
              <div className="card-header">
                <h3>Admin Permissions</h3>
                <p>Your access rights and permissions</p>
              </div>
              <div className="card-body">
                <div className="permissions-grid">
                  {permissions.map((perm, idx) => (
                    <div key={idx} className="permission-item">
                      <CheckCircle size={16} className={perm.granted ? 'granted' : 'denied'} />
                      <span>{perm.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Change Password Modal */}
      {showChangePassword && (
        <div className="modal-overlay" onClick={() => setShowChangePassword(false)}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Change Password</h3>
              <button className="modal-close" onClick={() => setShowChangePassword(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Current Password</label>
                <div className="password-input">
                  <input 
                    type={showCurrentPassword ? "text" : "password"} 
                    className="form-input" 
                    placeholder="Enter current password"
                  />
                  <button onClick={() => setShowCurrentPassword(!showCurrentPassword)} className="password-toggle">
                    {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              <div className="form-group">
                <label>New Password</label>
                <div className="password-input">
                  <input 
                    type={showNewPassword ? "text" : "password"} 
                    className="form-input" 
                    placeholder="Enter new password"
                  />
                  <button onClick={() => setShowNewPassword(!showNewPassword)} className="password-toggle">
                    {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              <div className="form-group">
                <label>Confirm New Password</label>
                <div className="password-input">
                  <input 
                    type={showConfirmPassword ? "text" : "password"} 
                    className="form-input" 
                    placeholder="Confirm new password"
                  />
                  <button onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="password-toggle">
                    {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowChangePassword(false)}>Cancel</button>
              <button className="btn-primary" onClick={handleChangePassword}>Change Password</button>
            </div>
          </div>
        </div>
      )}

      
    </div>
  );
};

export default AdminProfile;